/**
 * Determines if the user should see a an American date format for a given
 * countryCode.
 *
 * This is to fix an issue with shorthand dates showing the day before the month
 * in shorthand date notation.
 *
 * TODO(ryan@): Fix this hacky solution. Should transition to a more generic
 *             solution (such as react-intl or FormatJS) when we expand to more
 *             locales/run into more cases where locale affects the layout.
 */
export const DEFAULT_VIRTUAL_EVENT_FORMAT = 'ddd Do MMM YYYY';
export const VIRTUAL_EVENT_FORMAT_DATE_RANGE = 'Do MMM YYYY';
export const VIRTUAL_EVENT_FORMAT_DATE_RANGE_NO_YEAR = 'Do MMM';

export const shouldUseAmericanDateFormat = (countryCode) => {
  const supportedAmericanCountryCodes = ['US'];
  return supportedAmericanCountryCodes.includes(countryCode);
};

function concatDateStrings(day, month, year, countryCode) {
  return `${
    shouldUseAmericanDateFormat(countryCode)
      ? `${month}/${day}`
      : `${day}/${month}`
  }/${year}`;
}

/*
 * Format date timestamp in 12hr format, e.g. 8.01am, 11.34pm.
 */
export const formatDateTime = (timestamp) => {
  const date = new Date(timestamp);
  let hour = date.getUTCHours();
  let minute = date.getUTCMinutes();
  const amPM = hour > 11 ? 'pm' : 'am';
  if (hour > 12) {
    hour -= 12;
  } else if (hour === 0) {
    hour = '12';
  }
  if (minute < 10) {
    minute = `0${minute}`;
  }
  return `${hour}:${minute}${amPM}`;
};

export const formatDateTimeNoUTC = (timestamp) => {
  const date = new Date(timestamp);
  let hour = date.getHours();
  let minute = date.getMinutes();
  const amPM = hour > 11 ? 'pm' : 'am';
  if (hour > 12) {
    hour -= 12;
  } else if (hour === 0) {
    hour = '12';
  }
  if (minute < 10) {
    minute = `0${minute}`;
  }
  return `${hour}:${minute}${amPM}`;
};

/**
 * Format numbers as ordinals, e.g. 1st, 2nd, 3rd, 4th.
 */
export const formatDateOrdinal = (num) => {
  if (num > 3 && num < 21) {
    return 'th';
  }
  switch (num % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

/**
 * Format month name. E.g. January, February, ...
 */
export const formatDateMonthFull = (timestamp) => {
  const date = new Date(timestamp);
  const month = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ][date.getUTCMonth()];
  return month;
};
export const formatDateMonthFullNoUTC = (timestamp) => {
  const date = new Date(timestamp);
  const month = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ][date.getMonth()];
  return month;
};

/**
 * Format month as abbreviated, e.g. Jan, Feb, Mar.
 */
export const formatDateMonth = (timestamp) => {
  return formatDateMonthFull(timestamp).substring(0, 3);
};
export const formatDateMonthNoUTC = (timestamp) => {
  return formatDateMonthFullNoUTC(timestamp).substring(0, 3);
};

/**
 * Format day as 12th, 1st, 2nd, 23rd
 */
export const formatDayLong = (timestamp) => {
  const date = new Date(timestamp);
  const day = date.getUTCDate();
  return `${day}${formatDateOrdinal(day)}`;
};
export const formatDayLongNoUTC = (timestamp) => {
  const date = new Date(timestamp);
  const day = date.getDate();
  return `${day}${formatDateOrdinal(day)}`;
};

/**
 * Format date timestamp to 22nd Nov.
 * Does not include Year, under any circumstances.
 */
export const formatDateLongWithoutYear = (timestamp) => {
  const date = new Date(timestamp);
  const month = formatDateMonth(timestamp);
  const day = date.getUTCDate();
  return `${day}${formatDateOrdinal(day)} ${month}`;
};
export const formatDateLongWithoutYearNoUTC = (timestamp) => {
  const date = new Date(timestamp);
  const month = formatDateMonth(timestamp);
  const day = date.getDate();
  return `${day}${formatDateOrdinal(day)} ${month}`;
};

/**
 * Format date timestamp to 22nd Nov or 22nd Nov 2003.
 * Year is shown if the date is in the past, or the year is a future year.
 */
export const formatDateLong = (
  timestamp,
  currentDate = new Date() /* Set date for tests */,
) => {
  const date = new Date(timestamp);
  const year = date.getUTCFullYear();
  return `${formatDateLongWithoutYear(timestamp)} ${
    date < currentDate || year > currentDate.getUTCFullYear() ? year : ''
  }`.trim();
};
export const formatDateLongNoUTC = (
  timestamp,
  currentDate = new Date() /* Set date for tests */,
) => {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  return `${formatDateLongWithoutYearNoUTC(timestamp)} ${
    date < currentDate || year > currentDate.getUTCFullYear() ? year : ''
  }`.trim();
};

/**
 * Format date range in long format showing least information:
 * collapse month and year if applicable
 * eg 13th - 18th Jan 2020
 * eg 19th Jan - 2nd Feb 2020
 * eg 20th Jan 2020 - 20th Jan 2021
 */
export const formatDateRangeLong = (
  startTimestamp,
  endTimestamp,
  showTime = false,
) => {
  const startDate = new Date(startTimestamp);
  const startYear = startDate.getUTCFullYear();
  const startMonth = formatDateMonth(startTimestamp);
  const endDate = new Date(endTimestamp);
  const endYear = endDate.getUTCFullYear();
  const endMonth = formatDateMonth(endTimestamp);
  const sameYear = startYear === endYear;
  const sameMonth = startMonth === endMonth;
  return `
    ${showTime ? formatDateTime(startTimestamp) : ''}
    ${formatDayLong(startTimestamp)}
    ${sameMonth && sameYear ? '' : startMonth}
    ${sameYear ? '' : startYear}
    - ${showTime ? formatDateTime(endTimestamp) : ''}
    ${formatDayLong(endTimestamp)}
    ${endMonth} ${endYear}
  `;
};
export const formatDateRangeLongNoUTC = (
  startTimestamp,
  endTimestamp,
  showTime = false,
) => {
  const startDate = new Date(startTimestamp);
  const startYear = startDate.getFullYear();
  const startMonth = formatDateMonthNoUTC(startTimestamp);
  const endDate = new Date(endTimestamp);
  const endYear = endDate.getFullYear();
  const endMonth = formatDateMonthNoUTC(endTimestamp);
  const sameYear = startYear === endYear;
  const sameMonth = startMonth === endMonth;
  return `
    ${showTime ? formatDateTimeNoUTC(startTimestamp) : ''}
    ${formatDayLongNoUTC(startTimestamp)}
    ${sameMonth && sameYear ? '' : startMonth}
    ${sameYear ? '' : startYear}
    - ${showTime ? formatDateTimeNoUTC(endTimestamp) : ''}
    ${formatDayLongNoUTC(endTimestamp)}
    ${endMonth} ${endYear}
  `;
};

/**
 * Format date timestamp to 22/11/2003.
 */
export const formatDateShort = (timestamp, countryCode = '') => {
  const date = new Date(timestamp);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1;
  const day = date.getUTCDate();
  return concatDateStrings(
    `${day < 10 ? `0${day}` : day}`,
    `${month < 10 ? `0${month}` : month}`,
    `${year}`,
    countryCode,
  );
};

/**
 * Format date timestamp to 22/11/03.
 */
export const formatDateShorter = (timestamp, countryCode = '') => {
  const date = new Date(timestamp);
  const year = date
    .getUTCFullYear()
    .toString()
    .slice(2);
  const month = date.getUTCMonth() + 1;
  const day = date.getUTCDate();
  return concatDateStrings(
    `${day < 10 ? `0${day}` : day}`,
    `${month < 10 ? `0${month}` : month}`,
    `${year}`,
    countryCode,
  );
};

/**
 * Format number to string with commas and four decimal places by default.
 */
export const formatNumber = (
  num,
  options = { minimumFractionDigits: 0, maximumFractionDigits: 4 },
) => num.toLocaleString('en', options);

/**
 * Format prices to two decimal places, unless round.
 */
export const formatPrice = (symbol, value, numericalZero) => {
  const float = parseFloat(value);
  const formatted =
    float === parseInt(float, 10)
      ? formatNumber(float)
      : formatNumber(float, { minimumFractionDigits: 2 });
  if (float === 0 && !numericalZero) {
    return 'Free';
  }
  return `${symbol}${formatted}`;
};

/**
 * Format total reviews label
 * @param {string} totalReviews
 * @returns {string}
 */
export const getTotalReviewsLabel = (totalReviews) => {
  if (!totalReviews || totalReviews === 0) return '';

  const formattedReviews = totalReviews.toLocaleString('en');
  return totalReviews === 1
    ? `${formattedReviews} review`
    : `${formattedReviews} reviews`;
};

/**
 * Format events results label
 * @returns {string}
 */
export const getNumberOfResultsLabel = (currentPage, resultsPerPage, total) => {
  const start = (currentPage - 1) * resultsPerPage + 1;
  const end = currentPage * resultsPerPage;
  return `Showing ${formatNumber(start)} - ${formatNumber(
    end > total ? total : end,
  )} of ${formatNumber(total)} events`;
};

export const formatVirtualEventDate = (
  momentDate,
  format = DEFAULT_VIRTUAL_EVENT_FORMAT,
) => {
  return momentDate?.format?.(format);
};

export const formatSubmissionWindowEndDate = (
  momentDate,
  format = DEFAULT_VIRTUAL_EVENT_FORMAT,
) => {
  return momentDate
    ?.clone()
    ?.subtract(1, 'minute')
    ?.format?.(format);
};

export const formatDuration = (duration) => {
  const hours =
    duration
      ?.hours()
      ?.toString()
      ?.padStart(2, '0') || '00';
  const minutes =
    duration
      ?.minutes()
      ?.toString()
      ?.padStart(2, '0') || '00';
  const seconds =
    duration
      ?.seconds()
      ?.toString()
      ?.padStart(2, '0') || '00';
  return `${hours}:${minutes}:${seconds}`;
};
